import { GET_ALL_LOCATIONS } from "services/graphQL/ciq-queries";
import { useMemo, useState, useContext, useEffect, useCallback } from "react";
import { ProjectContext } from "context/ProjectProvider";
import { buildHierarchy, formatLBSTreeData } from "utils/utils";
import { getLBSTree } from "services/common";
import { AgGridReact } from "ag-grid-react";
import { useCIQQuery } from "./ciq-gql-hooks";

interface LocationWithPath {
  path: string;
  name: string;
  id: string;
}

interface LocationsResponse {
  get_locations_with_paths: LocationWithPath[];
}

type Props = {
  gridRef: React.RefObject<AgGridReact<any>>;
  projectId: string;
  hasPermissionToSetLBSForMaterial: boolean;
  entityList: Array<any> | undefined;
  locationDataKey?: string;
};

export const useLocationBreakdownV2Structure = ({
  gridRef,
  projectId,
  hasPermissionToSetLBSForMaterial,
  entityList,
  locationDataKey = "locations"
}: Props) => {
  const {
    isFeatureFlagEnabled,
    eventLogs,
    tokenRetrievalState,
    gqlClientForProject
  } = useContext(ProjectContext);

  const isLBSFeatureEnabled = isFeatureFlagEnabled(
    "LOCATION_BREAKDOWN_STRUCTURE"
  );

  const [LBSDataInTreeFormat, setLBSDataInTreeFormat] = useState<any[]>([]);

  const { data: LBSLocationsResponse, refetch: refetchLBSLocations } =
    useCIQQuery<LocationsResponse>(GET_ALL_LOCATIONS, {
      client: gqlClientForProject,
      skip:
        !gqlClientForProject ||
        !isLBSFeatureEnabled ||
        !hasPermissionToSetLBSForMaterial
    });

  const getLBSTreeData = useCallback(async () => {
    if (!isLBSFeatureEnabled || !hasPermissionToSetLBSForMaterial) return;
    const res = await getLBSTree(tokenRetrievalState?.token);
    if (res?.success) {
      const formattedTreeData = formatLBSTreeData(
        res?.data?.children.sort((a: any, b: any) =>
          a.name.localeCompare(b.name)
        ),
        (node) => ({
          ...node,
          value: node.id,
          title: node.name,
          children: []
        })
      );
      setLBSDataInTreeFormat(formattedTreeData);
    }
  }, [
    tokenRetrievalState?.token,
    isLBSFeatureEnabled,
    hasPermissionToSetLBSForMaterial
  ]);

  const LBSOptionsMaster: { label: string; value: string }[] = useMemo(() => {
    if (LBSLocationsResponse?.get_locations_with_paths?.length) {
      return LBSLocationsResponse?.get_locations_with_paths?.map(
        (location: LocationWithPath) => ({
          label: location.path,
          value: location.id
        })
      );
    }
    return [];
  }, [LBSLocationsResponse]);

  useEffect(() => {
    if (!tokenRetrievalState?.token) return;
    getLBSTreeData();
  }, [getLBSTreeData, tokenRetrievalState?.token]);

  useEffect(() => {
    if (eventLogs.length > 0) {
      const eventLogForLBSMaster = eventLogs.find(
        (eventLog) => eventLog.data_source === "location"
      );

      if (eventLogForLBSMaster) {
        refetchLBSLocations();
        getLBSTreeData();
      }
    }
  }, [eventLogs, projectId, refetchLBSLocations, getLBSTreeData]);

  const LBSLocationsFromMaterials = useMemo(() => {
    if (!isLBSFeatureEnabled || !entityList) return [];

    const locationsPaths: string[] = entityList?.map((material: any) => {
      if (!material[locationDataKey] || material[locationDataKey].length === 0)
        return [""];
      return material[locationDataKey]?.map((location: any) => {
        return location.path || "";
      });
    });

    const singleLocationPathArray = locationsPaths.flat();
    const uniqueLocationPathArray = [...new Set(singleLocationPathArray)];
    return uniqueLocationPathArray;
  }, [entityList, isLBSFeatureEnabled, locationDataKey]);

  const LBSTreeData = useMemo(() => {
    if (!isLBSFeatureEnabled || !entityList) return [];

    if (LBSLocationsFromMaterials.length > 0) {
      const treeData: any = buildHierarchy(LBSLocationsFromMaterials);
      return formatLBSTreeData(treeData, (node) => {
        return {
          ...node,
          key: node.path,
          title: node.name || "(Blanks)",
          children: []
        };
      });
    }
    return [];
  }, [isLBSFeatureEnabled, entityList, LBSLocationsFromMaterials]);

  const updateLBSFilter = useCallback(() => {
    try {
      gridRef.current?.api
        ?.getColumnFilterInstance(locationDataKey)
        .then((filterInstance: any) => {
          if (filterInstance?.componentInstance) {
            filterInstance.componentInstance.setDataForFilter({
              treeFilterStructure: LBSTreeData,
              treeFilterSelectedNodes: LBSLocationsFromMaterials
            });
          }
        });
    } catch (ex) {
      console.error("error in updateLBSFilter", ex);
    }
  }, [gridRef, LBSTreeData, LBSLocationsFromMaterials, locationDataKey]);

  const handleOnFilterOpen = async (event: any) => {
    if (event.column.colId === locationDataKey) {
      updateLBSFilter();
    }
  };

  return {
    isLBSFeatureEnabled,
    LBSOptionsMaster,
    LBSDataInTreeFormat,
    handleOnFilterOpen
  };
};
